import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BetslipConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { CalculatedOdds, Odds, OddsFormatterService, OddsOperations } from '@frontend/sports/odds/feature';
import { getLegsCount, isBetBuilderPick } from 'packages/sports/common/betslip/core/utils';
import { EpcotConfigService } from 'packages/sports/web/app/src/common/epcot-config.service';

import { BetslipSummaryService } from '../../../../summary/services/betslip-summary.service';
import { IBetslipOverAskPickComponentProps } from '../../../models/over-ask.model';
import { BetslipOverAskPickListBaseComponent } from '../../betslip-over-ask-pick-list-base.component';

@Component({
    selector: 'bs-digital-over-ask-pick-list',
    templateUrl: './betslip-over-ask-pick-list.component.html',
    styleUrls: ['./betslip-over-ask-pick-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BetslipOverAskPickListComponent extends BetslipOverAskPickListBaseComponent implements OnInit {
    title = '';
    legsCount = '';
    totalOdds: CalculatedOdds | null;
    changedTotalOdds: CalculatedOdds | null;
    hasOddsChange = false;

    shouldShowHeader = computed(
        () =>
            this.pickViewModel.length > 1 &&
            this.epcotConfigService.isEnabled() &&
            this.pickViewModel.some((pick) => isBetBuilderPick(pick.overAskPick.pick)) &&
            this.betslipConfig.allowBetbuilderCombos,
    );

    constructor(
        private oddsFormatter: OddsFormatterService,
        private sitecore: Sitecore,
        private epcotConfigService: EpcotConfigService,
        private betslipConfig: BetslipConfig,
        private betslipSummaryService: BetslipSummaryService,
        private destroyRef: DestroyRef,
    ) {
        super();
    }

    ngOnInit(): void {
        this.title = this.sitecore.betslip.messages.BetbuilderComboHeaderTitle;
        this.legsCount = interpolateDoubleBracesString(this.sitecore.betslip.linear.LegParlay, { count: this.getPicksLength(this.pickViewModel) });

        this.betslipSummaryService
            .getOverAskSummary$()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                const offerBetslipSummary = value.offer[1];
                const originalBetslipSummary = value.original[1];
                this.totalOdds = originalBetslipSummary.totalOdds;
                this.changedTotalOdds = OddsOperations.isOddsEmpty(offerBetslipSummary.totalOdds)
                    ? originalBetslipSummary.totalOdds
                    : offerBetslipSummary.totalOdds;
                this.hasOddsChange = !OddsOperations.equal2(this.totalOdds, this.changedTotalOdds);
            });
    }

    calculateOverAskOdds(nativeOdds: Odds | undefined): CalculatedOdds {
        return OddsOperations.toCalculatedOdds(nativeOdds);
    }

    multiplyOverAskOdds(prices: CalculatedOdds[]): CalculatedOdds {
        return OddsOperations.multiply(prices, true);
    }

    formattedOdds(odds: CalculatedOdds | null): string {
        return this.oddsFormatter.format(odds, { displayFormat: this.oddsDisplayFormat, isSummary: true });
    }

    getPicksLength(props: IBetslipOverAskPickComponentProps[]): number {
        return getLegsCount(props.map((prop) => prop.overAskPick.pick));
    }

    getPrice(odds: Odds): string {
        return this.oddsFormatter.format(odds, { displayFormat: this.oddsDisplayFormat, isSummary: true });
    }
}
